import React, { useState, useEffect } from 'react';
import { Button } from '@/components';
import { Provider, updateProvider, deleteProvider } from '@/services/api/providers';
import { toast } from '@/design-system/toaster';
import DeleteConfirmModal from './DeleteConfirmModal';
import { useNavigate } from 'react-router-dom';

interface ProviderDetailTabProps {
  provider: Provider;
  onRefresh: () => void;
}

const REVENUE_STREAMS = [
  { value: 'X_DOCK', label: 'X-Dock' },
  { value: 'SCHEDULED_INSTANT', label: 'Scheduled Instant' },
  { value: '4_WHEEL', label: '4 Wheel' },
];

export const ProviderDetailTab: React.FC<ProviderDetailTabProps> = ({
  provider,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<Partial<Provider>>({});
  const [showSecret, setShowSecret] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (provider) {
      setFormData({
        name: provider.name ?? '',
        legal_name: provider.legal_name ?? '',
        revenue_stream: provider.revenue_stream ?? 'EXPRESS',
        business_type: provider.business_type ?? 'COMPANY',
        contact_person: provider.contact_person ?? '',
        phone_number: provider.phone_number ?? provider.contact_phone_number ?? '',
        email: provider.email ?? '',
        tax_identification_number: provider.tax_identification_number ?? '',
        identification_number: provider.identification_number ?? '',
        address: provider.address ?? '',
        province: provider.province ?? '',
        city: provider.city ?? '',
        district: provider.district ?? '',
        sub_district: provider.sub_district ?? '',
        zip_code: provider.zip_code ?? '',
      });
    }
  }, [provider]);

  const handleChange = (field: keyof Provider, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Normalization for backend payload
      const payload: any = {
        name: formData.name,
        legalName: formData.legal_name,
        revenueStream: formData.revenue_stream,
        contactPerson: formData.contact_person,
        phoneNumber: formData.phone_number,
        email: formData.email,
        taxIdentificationNumber: formData.tax_identification_number,
        identificationNumber: formData.identification_number,
        address: formData.address,
        province: formData.province,
        city: formData.city,
        district: formData.district,
        subDistrict: formData.sub_district,
        zipCode: formData.zip_code,
      };

      await updateProvider(provider.id, payload);
      toast.success('Client details saved successfully');
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteProvider(provider.id);
      toast.success('Client deleted successfully');
      setDeleteOpen(false);
      navigate('/providers');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete client');
    } finally {
      setIsDeleting(false);
    }
  };

  const inputCls =
    'w-full rounded-gsm border border-stroke-soft-200 bg-white px-3 py-2 text-sm text-text-strong-950 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-bg-weak-50 disabled:text-text-sub-600';
  const labelCls =
    'mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600';

  return (
    <div className="space-y-6">
      {/* General Company Information */}
      <div className="rounded-gsm border border-stroke-soft-200 bg-white p-6">
        <h2 className="text-base font-bold tracking-tight text-text-strong-950 mb-1">
          Company Profile
        </h2>
        <p className="text-xs text-text-sub-600 leading-relaxed mb-6">
          Basic identity and registered business classification.
        </p>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Client Name</label>
              <input
                type="text"
                value={formData.name ?? ''}
                onChange={(e) => handleChange('name', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Legal Name</label>
              <input
                type="text"
                value={formData.legal_name ?? ''}
                onChange={(e) => handleChange('legal_name', e.target.value)}
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Business Type</label>
              <input
                type="text"
                value={formData.business_type ?? 'COMPANY'}
                disabled
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Business Unit</label>
              <select
                value={formData.revenue_stream ?? 'EXPRESS'}
                onChange={(e) => handleChange('revenue_stream', e.target.value)}
                className={inputCls}
              >
                {REVENUE_STREAMS.map((rs) => (
                  <option key={rs.value} value={rs.value}>
                    {rs.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Contact Person</label>
              <input
                type="text"
                value={formData.contact_person ?? ''}
                onChange={(e) => handleChange('contact_person', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Contact Phone</label>
              <input
                type="tel"
                value={formData.phone_number ?? ''}
                onChange={(e) =>
                  handleChange('phone_number', e.target.value.replace(/\D/g, ''))
                }
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className={labelCls}>Email</label>
              <input
                type="email"
                value={formData.email ?? ''}
                onChange={(e) => handleChange('email', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>NPWP (Tax ID)</label>
              <input
                type="text"
                value={formData.tax_identification_number ?? ''}
                onChange={(e) =>
                  handleChange(
                    'tax_identification_number',
                    e.target.value.replace(/\D/g, '')
                  )
                }
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>NIB</label>
              <input
                type="text"
                value={formData.identification_number ?? ''}
                onChange={(e) =>
                  handleChange(
                    'identification_number',
                    e.target.value.replace(/\D/g, '')
                  )
                }
                className={inputCls}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Address Information */}
      <div className="rounded-gsm border border-stroke-soft-200 bg-white p-6">
        <h2 className="text-base font-bold tracking-tight text-text-strong-950 mb-1">
          Registered Address
        </h2>
        <p className="text-xs text-text-sub-600 leading-relaxed mb-6">
          Physical location and territorial codes.
        </p>

        <div className="space-y-4">
          <div>
            <label className={labelCls}>Company Address</label>
            <textarea
              rows={3}
              value={formData.address ?? ''}
              onChange={(e) => handleChange('address', e.target.value)}
              className={inputCls}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className={labelCls}>Province</label>
              <input
                type="text"
                value={formData.province ?? ''}
                onChange={(e) => handleChange('province', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>City</label>
              <input
                type="text"
                value={formData.city ?? ''}
                onChange={(e) => handleChange('city', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Postal Code</label>
              <input
                type="text"
                value={formData.zip_code ?? ''}
                onChange={(e) =>
                  handleChange('zip_code', e.target.value.replace(/\D/g, ''))
                }
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>District (Kecamatan)</label>
              <input
                type="text"
                value={formData.district ?? ''}
                onChange={(e) => handleChange('district', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Sub-District (Kelurahan)</label>
              <input
                type="text"
                value={formData.sub_district ?? ''}
                onChange={(e) => handleChange('sub_district', e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Client API Credentials */}
      <div className="rounded-gsm border border-stroke-soft-200 bg-white p-6">
        <h2 className="text-base font-bold tracking-tight text-text-strong-950 mb-1">
          Client API Credentials
        </h2>
        <p className="text-xs text-text-sub-600 leading-relaxed mb-6">
          Read-only system identifiers and authentication tokens for integration.
        </p>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Client Code</label>
              <input
                type="text"
                value={provider.code ?? '-'}
                readOnly
                disabled
                className={`${inputCls} font-mono text-[13px]`}
              />
            </div>
            <div>
              <label className={labelCls}>Client Key</label>
              <input
                type="text"
                value={provider.client_key ?? '-'}
                readOnly
                disabled
                className={`${inputCls} font-mono text-[13px]`}
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>Client Secret</label>
            <div className="relative">
              <input
                type={showSecret ? 'text' : 'password'}
                value={provider.client_secret ?? '-'}
                readOnly
                disabled
                className={`${inputCls} font-mono text-[13px] pr-10`}
              />
              <button
                type="button"
                onClick={() => setShowSecret((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-sub-600 hover:text-text-strong-950 transition-colors"
                aria-label={showSecret ? 'Hide secret' : 'Show secret'}
              >
                <i
                  className={`ri-${
                    showSecret ? 'eye-off-line' : 'eye-line'
                  } text-[18px]`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Action Footer */}
      <div className="flex items-center justify-between border-t border-stroke-soft-200 pt-5">
        <Button
          variant="outline-danger"
          onClick={() => setDeleteOpen(true)}
          disabled={isSaving}
        >
          <i className="ri-delete-bin-line mr-1.5" />
          Delete Client
        </Button>

        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving Changes…' : 'Save Changes'}
        </Button>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Delete Client?"
        subtitle={`Deleting ${provider.name}`}
        description="Are you sure you want to permanently delete this client? All associated outlets, configurations, and pricing schemes will be deleted."
        confirmText="Yes, Delete Client"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ProviderDetailTab;
